import { IntroductoryPricing } from "@/models/IntroductoryPricing"
import { PriceType } from "@/models/PriceType"
import { getUnitDurationString, PricePeriod } from "@/models/PricePeriod"
import { PriceFeeType } from "@/models/PriceFeeType"
import { PriceStartType } from "@/models/PriceStartType"
import { PriceStartAnchor } from "@/models/PriceStartAnchor"

import { type JSONSchemaType } from "ajv"

export interface Price {
  id: string
  unitAmount: number
  originalAmount?: number
  hidden?: boolean
  type?: PriceType
  termsID?: string
  currency: "AUD" | "USD" | "CAN" | "NZD" | "EUR" | "GBP"
  cohortDate?: string
  fee?: number
  feeType?: PriceFeeType
  period?: PricePeriod
  startType: PriceStartType
  introductoryPricing?: IntroductoryPricing
  iterations?: number
  startAnchor?: PriceStartAnchor
  defaultEffectiveCycles?: number
}

// Helper to determine if a price is temporary
export function isTemporaryPrice(price: Price): boolean {
  return (
    price.defaultEffectiveCycles !== undefined &&
    price.defaultEffectiveCycles !== null
  )
}

export const basePriceString = (price: Price, period: PricePeriod): string => {
  return `${formatCurrency(price)} / ${getUnitDurationString(period)}`
}

// Format price string according to price type and period
export function getPricingString(price: Price): string {
  const { period, iterations } = price
  if (period) {
    const baseString = basePriceString(price, period)

    if (iterations) {
      if (iterations === 1) {
        const unitString = period.duration > 1 ? "s" : ""
        return `${formatCurrency(price)} for ${period.duration} ${period.unit}${unitString}`
      } else {
        return `${baseString} for ${iterations} payments`
      }
    } else {
      return baseString
    }
  } else {
    return formatCurrency(price)
  }
}

// Helper to format currency
function formatCurrency(price: Price): string {
  const formatted = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: price.currency,
    currencyDisplay: "narrowSymbol",
  }).format(price.unitAmount / 100)
  // console.log({ formatted })
  return formatted
}

export const schema: JSONSchemaType<Price> = {
  $id: "https://newie.app/schemas/price.json",
  type: "object",
  properties: {
    id: { type: "string" },
    iterations: { type: "number", nullable: true },
    unitAmount: { type: "number" },
    originalAmount: { type: "number", nullable: true },
    hidden: { type: "boolean", nullable: true },
    termsID: { type: "string", nullable: true },
    fee: { type: "number", nullable: true },
    cohortDate: { type: "string", nullable: true },
    period: { $ref: "https://newie.app/schemas/pricePeriod.json" },
    introductoryPricing: {
      $ref: "https://newie.app/schemas/introductoryPricing.json",
    },
    startAnchor: { $ref: "https://newie.app/schemas/priceStartAnchor.json" },
    startType: {
      type: "string",
      enum: ["IMMEDIATE", "ABSOLUTE", "RELATIVE", "DELAYED"],
    },
    type: {
      type: "string",
      enum: ["INSTALLMENT", "FIXED_TERM", "REGULAR"],
      nullable: true,
    },
    currency: {
      type: "string",
      enum: ["AUD", "USD", "CAN", "NZD", "EUR", "GBP"],
    },
    feeType: { type: "string", enum: ["absorb", "passOn"], nullable: true },
    defaultEffectiveCycles: { type: "number", nullable: true },
  },
  required: ["id", "unitAmount", "startType", "currency"],
} as const
